import { useState, useEffect, useCallback } from 'react';

export const useScreenFeatures = () => {
    const [wakeLock, setWakeLock] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const requestWakeLock = useCallback(async () => {
        try {
            if ('wakeLock' in navigator) {
                const lock = await navigator.wakeLock.request('screen');
                setWakeLock(lock);
            }
        } catch (err) {
            console.error('Wake Lock request failed:', err);
        }
    }, []);

    const releaseWakeLock = useCallback(async () => {
        if (wakeLock) {
            try {
                await wakeLock.release();
                setWakeLock(null);
            } catch (err) {
                console.error('Wake Lock release failed:', err);
            }
        }
    }, [wakeLock]);

    const requestFullscreen = useCallback(async () => {
        try {
            const element = document.documentElement;
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

            if (isIOS) {
                // Limited iOS support, usually requires user action and specific styling
                element.style.position = 'fixed';
                element.style.width = '100%';
                element.style.height = '100%';
                element.style.top = '0';
                element.style.left = '0';
                element.style.zIndex = '9999';
                setIsFullscreen(true);
            } else if (element.requestFullscreen) {
                await element.requestFullscreen();
            } else if (element.webkitRequestFullscreen) {
                await element.webkitRequestFullscreen();
            } else if (element.msRequestFullscreen) {
                await element.msRequestFullscreen();
            }
        } catch (err) {
            console.error('Fullscreen request failed:', err);
        }
    }, []);

    const exitFullscreen = useCallback(async () => {
        try {
            // iOS Cleanup
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
            if (isIOS) {
                const element = document.documentElement;
                element.style.position = '';
                element.style.width = '';
                element.style.height = '';
                element.style.top = '';
                element.style.left = '';
                element.style.zIndex = '';
                setIsFullscreen(false);
            }

            if (document.fullscreenElement) {
                if (document.exitFullscreen) {
                    await document.exitFullscreen();
                } else if (document.webkitExitFullscreen) {
                    await document.webkitExitFullscreen();
                } else if (document.msExitFullscreen) {
                    await document.msExitFullscreen();
                }
            }
        } catch (err) {
            console.error('Exit fullscreen failed:', err);
        }
    }, []);

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            // Cleanup wake lock on unmount if exists
            if (wakeLock) wakeLock.release().catch(e => console.error(e));

            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, [wakeLock]);

    return {
        requestWakeLock,
        releaseWakeLock,
        requestFullscreen,
        exitFullscreen,
        isFullscreen
    };
};
